<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import paperPattern from "~/features/computer/data/paper-pattern-2026.json";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";

const chapterLabels: Record<number, string> = {
  1: "Software Development",
  2: "Python",
  3: "Algorithms",
  4: "Data Structures",
  5: "Data Analytics",
  6: "Emerging Technologies",
  7: "Legal and Ethical Aspects",
  8: "Digital Literacy",
  9: "Entrepreneurship",
};

const chapterLinks: Record<number, string> = {
  1: "/11th/computer/sdlc/",
  2: "/11th/computer/python/",
  3: "/11th/computer/algorithm/",
  4: "/11th/computer/data-structures/",
  5: "/11th/computer/data-analytics/",
  6: "/11th/computer/emerging-tech/",
  7: "/11th/computer/legal-ethics/",
  8: "/11th/computer/digital-literacy/",
  9: "/11th/computer/entrepreneurship/",
};

const mcqRows = paperPattern.sections.part_1_objective.details.chapters.map((chapter, index) => ({
  chapter,
  label: chapterLabels[chapter],
  mcqs: paperPattern.sections.part_1_objective.details.distribution[index],
}));

const shortBlocks = paperPattern.sections.part_2_subjective.short_questions.map((item) => ({
  questionNo: item.question_no,
  attempt: item.attempt,
  marksPerQuestion: item.marks_per_question,
  chapters: item.chapter_distribution.chapters.map((chapter, index) => ({
    chapter,
    label: chapterLabels[chapter],
    questions: item.chapter_distribution.questions[index],
  })),
}));

const longBlocks = paperPattern.sections.part_2_subjective.long_questions.questions.map((item) => ({
  questionNo: item.q,
  chapter: item.chapter,
  label: chapterLabels[item.chapter],
}));

const snapshotCards = [
  { title: "MCQs", marks: paperPattern.overview.objective_marks, detail: "15 marks" },
  {
    title: "Short",
    marks:
      paperPattern.sections.part_2_subjective.short_questions.length *
      6 *
      paperPattern.sections.part_2_subjective.short_questions[0].marks_per_question,
    detail: "3 selective blocks",
  },
  {
    title: "Long",
    marks: 3 * paperPattern.sections.part_2_subjective.long_questions.marks_per_question,
    detail: paperPattern.sections.part_2_subjective.long_questions.attempt,
  },
];

const relatedPages = [
  {
    title: "11th Computer Model Paper 2026",
    description: "Model-paper style practice flow ke liye sample exam route.",
    to: "/11th/computer/practice/",
  },
  {
    title: "11th Computer Past Papers",
    description: "Past-paper intent ke liye final exam prep aur paper-style revision routes.",
    to: "/11th/computer/guess-paper/",
  },
  {
    title: "11th Computer Pairing Scheme",
    description: "Kis chapter se question aata hai us ka smart study view.",
    to: "/11th/computer/pairing-scheme/",
  },
];

const importantQuestionHooks = [
  {
    title: "Python Programs",
    description: "Chapter 2 ke programs aur solved flow yahan se kholo.",
    to: "/11th/computer/python/solved-exercise/",
  },
  {
    title: "Algorithms",
    description: "Most important algorithms aur related prep ke liye direct route.",
    to: "/11th/computer/algorithm/",
  },
  {
    title: "SDLC",
    description: "Chapter 1 ki high-intent short aur theory prep yahan milegi.",
    to: "/11th/computer/sdlc/",
  },
];

const previousPatterns = [
  { year: "Paper Pattern 2025", note: "Recent comparison keyword for pattern intent." },
  { year: "Paper Pattern 2024", note: "Older year variation jo search demand capture karti hai." },
];

const faqs = [
  {
    question: "11th computer paper pattern 2026 kya hai?",
    answer:
      "2026 paper pattern mein total 75 marks hote hain. MCQs 15 marks, short questions 36 marks aur long questions 24 marks ke hote hain.",
  },
  {
    question: "Class 11 computer paper mein kitne MCQs aate hain?",
    answer: "15 MCQs aate hain aur yeh tamam chapters se hote hain.",
  },
  {
    question: "11th computer short questions ka pattern kya hai?",
    answer:
      "Short questions 3 blocks mein hote hain (Q2, Q3, Q4). Har block different chapters se aata hai aur aapko 6 attempt karne hote hain.",
  },
  {
    question: "11th computer long questions kis chapter se aate hain?",
    answer:
      "Long questions fixed chapters se aate hain:\nQ5 -> Chapter 2\nQ6 -> Chapter 3\nQ7 -> Chapter 4\nQ8 -> Chapter 5\nQ9 -> Chapter 7",
  },
  {
    question: "11th computer pairing scheme aur paper pattern mein kya difference hai?",
    answer:
      "Paper pattern paper ka structure batata hai (MCQs, short, long), jab ke pairing scheme batati hai ke kis chapter se questions aayenge.",
  },
  {
    question: "11th computer paper scheme 2026 Punjab board kya hai?",
    answer:
      "Punjab board ke paper scheme mein MCQs tamam chapters se, short questions grouped chapters se aur long questions fixed chapters se diye jate hain.",
  },
  {
    question: "11th computer model paper aur paper pattern mein kya farq hai?",
    answer:
      "Paper pattern structure hota hai, jab ke model paper us structure ka sample hota hai.",
  },
  {
    question: "11th computer ke liye kaun se chapters important hain?",
    answer:
      "Chapter 2 (Python), Chapter 3, Chapter 4 aur Chapter 5 sab se zyada important hain kyunki long aur short dono mein aate hain.",
  },
];

function openPrintDialog() {
  if (import.meta.client) {
    window.print();
  }
}

usePageSeo({
  title: "11th Computer Paper Pattern 2026 (Punjab Board) - Scheme, MCQs, Long Questions | Ustaad",
  description:
    "11th computer paper pattern 2026 Punjab Board page for class 11 computer: MCQs distribution, short questions pairing scheme, long questions pattern, important chapters and printable paper scheme.",
  keywords:
    "11th computer paper pattern 2026, class 11 computer paper pattern, punjab board paper scheme, first year computer paper pattern 2026",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Paper Pattern", item: "/11th/computer/paper-pattern/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "11th Computer Paper Pattern 2026",
    items: [
      { name: "Chapter 2 Python", path: chapterLinks[2] },
      { name: "Chapter 3 Algorithms", path: chapterLinks[3] },
      { name: "Chapter 4 Data Structures", path: chapterLinks[4] },
      { name: "Chapter 5 Data Analytics", path: chapterLinks[5] },
      { name: "Pairing Scheme", path: "/11th/computer/pairing-scheme/" },
      { name: "Top 100 MCQs", path: "/11th/computer/mcqs/top-100/" },
      { name: "Important Questions", path: "/11th/computer/important-questions/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-amber-50 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 md:px-8 md:py-8 print:hidden">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">Decision Page</p>
      <h1 class="mt-3 max-w-3xl text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-5xl">11th Computer Paper Pattern 2026 (Punjab Board) - Scheme, MCQs, Long Questions</h1>
      <p class="mt-4 max-w-3xl text-base leading-8 text-slate-700 dark:text-slate-300">
        Class 11 computer paper pattern 2026 ka real breakdown:
        <span class="font-semibold text-slate-950 dark:text-white"> MCQs har chapter se</span>,
        <span class="font-semibold text-slate-950 dark:text-white"> short selective blocks</span> me,
        aur <span class="font-semibold text-slate-950 dark:text-white">long fixed chapters</span> se aate hain.
      </p>
      <p class="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
        Yeh Class 11 Computer paper pattern 2026 Punjab Board ke liye complete breakdown hai jisme MCQs distribution, short questions pairing aur long questions ke exact chapters bataye gaye hain.
      </p>
      <p class="mt-3 text-base font-semibold text-emerald-700">Agar aap Ch 2-5 prepare karein to paper control ho jata hai.</p>

      <div class="mt-6 flex flex-wrap gap-3">
        <a
          href="#full-pattern"
          class="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-sky-700"
        >
          View Full Pattern
        </a>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-sky-300 hover:text-sky-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-sky-600 dark:hover:text-sky-300"
          @click="openPrintDialog"
        >
          Download PDF
        </button>
      </div>
    </section>

    <section class="grid gap-4 md:grid-cols-3">
      <article
        v-for="item in snapshotCards"
        :key="item.title"
        class="rounded-[26px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6"
      >
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">{{ item.title }}</p>
        <h2 class="mt-3 text-4xl font-bold tracking-tight text-slate-950 dark:text-white">{{ item.marks }}</h2>
        <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.detail }}</p>
      </article>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">MCQs Distribution</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">MCQs Distribution</h2>
        </div>
        <p class="text-sm font-medium text-slate-500 dark:text-slate-400">15 total MCQs</p>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Punjab Board paper scheme me objective portion poori book se aata hai, is liye MCQs ko ignore nahi karna chahiye.</p>

      <div class="mt-4 overflow-hidden rounded-[18px] border border-slate-200 dark:border-slate-800">
        <table class="min-w-full divide-y divide-slate-200 text-left text-sm dark:divide-slate-800">
          <thead class="bg-slate-50 text-slate-700 dark:bg-slate-950 dark:text-slate-300">
            <tr>
              <th class="px-3 py-2 font-semibold">Chapter</th>
              <th class="px-3 py-2 font-semibold">MCQs</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 bg-white dark:divide-slate-800 dark:bg-slate-900">
            <tr v-for="row in mcqRows" :key="row.chapter">
              <td class="px-3 py-2 text-slate-900 dark:text-slate-100">
                <div class="text-sm font-medium leading-5">Ch {{ row.chapter }}</div>
                <NuxtLink :to="chapterLinks[row.chapter]" class="text-[11px] leading-4 text-sky-700 hover:text-sky-900 dark:text-sky-300 dark:hover:text-sky-200">{{ row.label }}</NuxtLink>
              </td>
              <td class="px-3 py-2 font-medium text-slate-900 dark:text-slate-100">{{ row.mcqs }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Short Questions</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Short Questions Pattern</h2>
        </div>
        <p class="text-sm font-medium text-amber-700">This is gold</p>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Short Questions Pairing Scheme (Class 11 Computer): Paper mein 3 blocks aate hain. Q2, Q3, Q4 har block different chapters se hota hai aur isi liye selective prep yahan bohat matter karti hai.</p>

      <div class="mt-5 grid gap-4 lg:grid-cols-3">
        <article
          v-for="item in shortBlocks"
          :key="item.questionNo"
          class="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <div class="flex items-start justify-between gap-3">
            <h3 class="text-xl font-semibold text-slate-950 dark:text-white">{{ item.questionNo }}</h3>
            <span class="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
              {{ item.attempt }}
            </span>
          </div>
          <p class="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">{{ item.marksPerQuestion }} marks each</p>
          <div class="mt-5 space-y-3">
            <div
              v-for="chapter in item.chapters"
              :key="`${item.questionNo}-${chapter.chapter}`"
              class="rounded-[18px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
            >
              <p class="text-lg font-semibold text-slate-950 dark:text-white">Ch {{ chapter.chapter }}</p>
              <NuxtLink :to="chapterLinks[chapter.chapter]" class="mt-1 block text-sm text-sky-700 hover:text-sky-900 dark:text-sky-300 dark:hover:text-sky-200">{{ chapter.label }}</NuxtLink>
              <p class="mt-2 text-sm font-semibold text-sky-700">{{ chapter.questions }} questions</p>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section id="full-pattern" class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Long Questions</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Long Questions Pattern</h2>
        </div>
        <p class="text-sm font-medium text-sky-700">Exam prediction engine</p>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Yahi wo section hai jahan student dekhna chahta hai ke long questions kis chapter se aate hain. Is pattern me fixed flow sab se strong signal hai.</p>

      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <article
          v-for="item in longBlocks"
          :key="item.questionNo"
          class="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-xl font-semibold text-slate-950 dark:text-white">{{ item.questionNo }}</h3>
          <p class="mt-4 text-lg font-semibold text-slate-900 dark:text-slate-100">Ch {{ item.chapter }}</p>
          <NuxtLink :to="chapterLinks[item.chapter]" class="mt-2 block text-sm leading-7 text-sky-700 hover:text-sky-900 dark:text-sky-300 dark:hover:text-sky-200">{{ item.label }}</NuxtLink>
        </article>
      </div>
    </section>

    <section class="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
      <div class="rounded-[28px] border border-slate-200 bg-slate-950 p-5 text-white md:p-6">
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-sky-300">Exam Shortcut</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight">Lazy Student Guide</h2>
        <div class="mt-5 space-y-3 text-sm leading-7 text-slate-200">
          <p>Ch 2 (Python)</p>
          <p>Ch 3</p>
          <p>Ch 4</p>
          <p>Ch 5</p>
        </div>
        <div class="mt-6 rounded-[22px] border border-white/10 bg-white/5 p-4 text-sm leading-7 text-slate-300">
          Agar aap sirf yeh chapters karein to long questions aur majority short questions cover ho jata hai.
        </div>
      </div>

      <div class="rounded-[28px] border border-amber-200 bg-amber-50 p-5 dark:border-amber-900/50 dark:bg-amber-950/20 md:p-6">
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-amber-700">What To Skip</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Low priority</h2>
        <div class="mt-5 space-y-3 text-sm leading-7 text-slate-700 dark:text-slate-300">
          <p>Ch 7</p>
          <p>Ch 8</p>
          <p>Ch 9</p>
        </div>
        <p class="mt-6 text-sm font-medium text-slate-700 dark:text-slate-300">Sirf MCQs ke liye padhein.</p>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Related</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Model Paper, Past Papers, Pairing Scheme</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          v-for="item in relatedPages"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Important Questions</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Most Important Questions From This Pattern</h2>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Is paper pattern ke basis par most important questions: Python programs, Algorithms aur SDLC. In routes ko next step ke taur par kholo.</p>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          v-for="item in importantQuestionHooks"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6 print:hidden">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Previous Patterns</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Paper Pattern 2025 and 2024</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-2">
        <article
          v-for="item in previousPatterns"
          :key="item.year"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.year }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.note }}</p>
        </article>
      </div>
    </section>

    <section class="rounded-[28px] border border-stone-200 bg-stone-50 px-5 py-6 dark:border-slate-800 dark:bg-slate-950 md:px-8 print:hidden">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500 dark:text-slate-400">PDF Block</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Printable version, same data</h2>
        </div>
        <div class="flex flex-wrap gap-3">
          <a
            href="#printable-pattern"
            class="inline-flex items-center justify-center rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
          >
            View PDF
          </a>
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:border-sky-300 hover:text-sky-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-sky-600 dark:hover:text-sky-300"
            @click="openPrintDialog"
          >
            Download PDF
          </button>
        </div>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6 print:hidden">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQs</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Class 11 Computer Paper Pattern FAQs</h2>
      </div>
      <div class="mt-5 space-y-4">
        <article
          v-for="faq in faqs"
          :key="faq.question"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
          <p class="mt-2 whitespace-pre-line text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
        </article>
      </div>
    </section>

    <section class="print:pt-0">
      <div
        id="printable-pattern"
        class="mx-auto max-w-[760px] rounded-[28px] border border-stone-200 bg-white px-6 py-8 shadow-[0_18px_60px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-900 md:px-12 md:py-12 print:max-w-none print:rounded-none print:border-0 print:px-0 print:py-0 print:shadow-none"
      >
        <div class="border-b border-dashed border-stone-300 pb-6 dark:border-slate-700">
          <p class="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500 dark:text-slate-400">Printable Paper Pattern</p>
          <h2 class="mt-3 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">{{ paperPattern.meta.title }} {{ paperPattern.meta.year }}</h2>
          <p class="mt-3 text-base leading-8 text-slate-700 dark:text-slate-300">
            Total marks {{ paperPattern.meta.total_marks }}, duration {{ paperPattern.meta.duration }}. MCQs sab chapters se aate hain, short 3 selective blocks me hotay hain, aur long fixed chapters se uthte hain.
          </p>
        </div>

        <div class="mt-8 grid gap-4 md:grid-cols-3">
          <section
            v-for="item in snapshotCards"
            :key="`print-${item.title}`"
            class="rounded-[22px] border border-stone-200 bg-stone-50 p-5 dark:border-slate-800 dark:bg-slate-950"
          >
            <h3 class="text-xl font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
            <p class="mt-3 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">{{ item.marks }}</p>
            <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.detail }}</p>
          </section>
        </div>

        <div class="mt-10">
          <h3 class="text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Long Question Map</h3>
          <div class="mt-4 space-y-3 text-base leading-8 text-slate-700 dark:text-slate-300">
            <p v-for="item in longBlocks" :key="`print-${item.questionNo}`">{{ item.questionNo }}: Ch {{ item.chapter }}</p>
          </div>
        </div>
      </div>
    </section>
  </PageContainer>
</template>
